import { useRef, useState } from 'react';
import { FolderOpen, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/features/chat/hooks/useIsMobile';

import { CollectionHeader } from './components/collections/CollectionHeader';
import { CollectionsStats } from './components/collections/CollectionsStats';
import { CreateCollectionModal } from './components/collections/CreateCollectionModal';
import { DocumentTable } from './components/documents/DocumentTable';
import { EmptyState } from './components/EmptyState';
import { Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { DuplicateFilesDialog } from './components/upload/DuplicateFilesDialog';
import { GlobalDropOverlay } from './components/upload/GlobalDropOverlay';
import { UploadQueuePanel } from './components/upload/UploadQueuePanel';
import { useUploadQueue } from './hooks/useDocuments';
import { useGlobalDropzone } from './hooks/useGlobalDropzone';
import { FILE_INPUT_ACCEPT } from './lib/uploadConstraints';

export function CollectionsPage() {
  const [selected, setSelected] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);
  const isMobile = useIsMobile();
  const mainRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { queue, enqueue, dismiss, duplicatePrompt, resolveDuplicates } = useUploadQueue(selected);
  const { isDragging } = useGlobalDropzone(mainRef, {
    enabled: Boolean(selected),
    onDropFiles: (files) => void enqueue(files),
    onRejected: () => toast.error('Selecione uma coleção primeiro.'),
  });

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  const sidebar = (
    <Sidebar selected={selected} onSelect={setSelected} onCreateClick={() => setCreateOpen(true)} />
  );

  return (
    <div className="bg-background text-foreground flex h-dvh flex-col font-sans">
      <TopBar
        mobileMenuSlot={
          isMobile && (
            <Sheet>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Abrir menu de coleções"
                  className="hover:bg-accent cursor-pointer rounded-md p-1.5"
                >
                  <FolderOpen className="size-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                {/* Radix exige Title/Description em todo Dialog. Aqui são só
                    para leitor de tela — o painel já se explica visualmente. */}
                <SheetHeader className="sr-only">
                  <SheetTitle>Coleções</SheetTitle>
                  <SheetDescription>Selecione uma coleção para gerenciar.</SheetDescription>
                </SheetHeader>
                {sidebar}
              </SheetContent>
            </Sheet>
          )
        }
        onCreateCollection={() => setCreateOpen(true)}
        onUploadClick={openFilePicker}
        uploadDisabled={!selected}
        onHome={() => setSelected('')}
      />

      <div className="flex flex-1 overflow-hidden">
        {!isMobile && <aside className="w-64 shrink-0 overflow-hidden border-r">{sidebar}</aside>}

        <main ref={mainRef} className="relative flex flex-1 flex-col overflow-hidden">
          <GlobalDropOverlay visible={isDragging} />

          {selected ? (
            <>
              <CollectionHeader collectionId={selected} />
              {/*
                Lista plana e alinhada ao mesmo gutter do cabeçalho (px-6),
                como no Drive. Antes era um card com `p-4` por fora e `px-6` por
                dentro, o que deslocava a tabela do cabeçalho acima dela.

                `key` força remount ao trocar de coleção. Sem isso o estado
                interno da tabela (Sheet de detalhe aberto, filtro de busca,
                ordenação, erros expandidos) vaza pra coleção seguinte — e um
                Sheet preso aberto deixa `pointer-events: none` no body, o que
                faz a página inteira parecer travada.
              */}
              <div className="min-h-0 flex-1">
                <DocumentTable
                  key={selected}
                  collectionId={selected}
                  onUploadClick={openFilePicker}
                />
              </div>
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-6 p-6">
              <CollectionsStats />
              <EmptyState
                icon={<FolderOpen />}
                title="Selecione uma coleção"
                description="Escolha uma coleção no painel esquerdo ou crie uma nova para começar."
                action={
                  <Button size="sm" onClick={() => setCreateOpen(true)}>
                    <Plus className="mr-1 size-4" />
                    Nova coleção
                  </Button>
                }
              />
            </div>
          )}
        </main>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="sr-only"
        accept={FILE_INPUT_ACCEPT}
        onChange={(e) => {
          if (e.target.files) void enqueue(Array.from(e.target.files));
          e.target.value = '';
        }}
      />

      <UploadQueuePanel queue={queue} onDismiss={dismiss} />
      <DuplicateFilesDialog
        prompt={duplicatePrompt}
        onResolve={(action) => void resolveDuplicates(action)}
      />
      <CreateCollectionModal open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
